/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-param-reassign */
import { useNavigate } from 'react-router-dom';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ColDef,
  FirstDataRenderedEvent, GetRowIdParams,
  ICellRendererParams,
  ProcessCellForExportParams,
  RowClickedEvent,
  RowGroupOpenedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import mainStyles from '../../pages/main.module.css';
import styles from './mainFrameRightSection.module.css';
import routes from '../../utils/routes';
import {
  useDocumentDisplayMode,
  useDocumentDownload,
  useDocumentGetDetail,
  useDocumentBatchPdfDownload,
} from '../../hooks/api/document.hook';
import {
  ContractFlag,
  Document, DocumentDetail, BatchPdfDownloadNameRules, SortList,
} from '../../services/http/documents.api';
import { Folder } from '../../services/http/folder.api';
import BaseTable from '../BaseTable';
import DropdownMenu from '../DropdownMenu';
import Button from '../Button/button';
// eslint-disable-next-line import/no-cycle
import DocDetailModalFrame from '../DocDetailModal/docDetailModalFrame';
import Breadcrumb from '../Breadcrumb';
import ExportLabelComponent from '../../pages/confirmedRequestListScreen/exportLabelComponent';
import ControlCode from '../../utils/controlCode';
import AGUtils from '../../utils/ag-grid.utils';
import PaginationJp from '../BaseTable/partials/PaginationJp/mannual';
import { useGetAllTagsApi } from '../../hooks/api/tag.hook';
import RelationalDocumentsDetailCellRenderer from '../BaseTable/partials/relationalDocumentsDetailCellRenderer/relationalDocumentsDetailCellRenderer';
import AlertModal, {
  alertModalInitialState, AlertModalProps,
} from '../AlertModal';
import { useMessageModal } from '../../hooks/modal.hook';
import { ApiError } from '../../services/http';
import LoadingOverlay from '../LoadingOverlay';
import { ManagementType, useAuthContext } from '../../store/auth.store';
import { wareHouseRecords, WarehouseStatusCode } from '../../utils/warehouse.utils';
import { useCreateDownloadFileLogApi, useCreateLogApi } from '../../hooks/api/log.hook';
import { LogFormName } from '../../utils/log.utils';
import CheckModalBody from '../CheckModalBody/checkModalBody';
import useEffectOnce from '../../hooks/useEffectOnce.hook';
import Checkbox from '../Checkbox';
import PdfIcon from '../../assets/images/pdf-icon.png';
import { STATUS_MESSAGES } from '../../utils/messages';
import { PdfPreviewModal } from '../PdfPreviewModal';
import { useReservationCartRegister } from '../../hooks/api/reservationCart.hook';
import { RegisterCartResponseMessageType, RequestType } from '../../services/http/reservationCart.api';
import renderCartIcon from './renderCartIcon';
import useDragAndDrop from '../../hooks/useDragAndDrop.hook';
import useTextHighlighter from '../../hooks/useTextHighlighter';
import BatchPdfDownloadModal from '../BatchPdfDownloadModal/BatchPdfDownloadModal';
import Formatter from '../../utils/formatters';
import BatchDocumentDownloadModal from '../BatchDocumentDownloadModal/BatchDocumentDownloadModal';
import renderRelatedDocumentIcon from './renderRelatedDocumentIcon';

export enum SearchType {
  FOLDER = 'folder',
  ADVANCED = 'advanced',
  SIMPLE = 'simple',
}

export interface DocumentsAndSearchType {
  documents: Document[];
  searchType: SearchType | null;
  searchKeyword?: string;
}

export const defaultDocumentsAndSearchType: DocumentsAndSearchType = {
  documents: [],
  searchType: null,
  searchKeyword: undefined,
};

export interface Pagination {
  page: number,
  totalItems: number,
  pageLimit: number,
  totalPages: number,
}

export const defaultPagination: Pagination = {
  page: 1,
  totalItems: 0,
  pageLimit: 0,
  totalPages: 0,
};

interface SelectedFilesToDocumentWarehousing {
  ccControlNumber: number,
  documentId: number,
  documentName: string,
  documentFileName?: string,
  documentFileSize?: number,
  documentTypeId?: number,
  documentTypeName?: string,
  tagList: { tagLabel: string, tagValue: string }[],
  relationalDocumentCount?: number,
  warehouseStatus?: string,
  warehouseStatusCode?: string,
  paperFlg?: ContractFlag,
  electronicFlg?: ContractFlag,
  registUser: string,
  registDate: Date,
  updateUser: string,
  updateDate: Date,
  itemCode: string,
  barcodePrinting: string,
  cartAddedFlg?: number,
}

export const mainPageTableName = 'mainPageTable_v1';

/**
 * メインフレームの右セクションのプロップスインターフェース
 */
export interface Props {
  /** 選択されているフォルダ */
  folder: Folder | null,
  /** フォルダの権限変更時のコールバック */
  onChangingFolderPermissions: () => void,
  // --------------------------------------------------
  documentsAndSearchType: DocumentsAndSearchType,
  pagination: Pagination,
  isSearching: boolean,
  highlightTexts: string[],
  ocrHighlightTexts: string[],
  isFolderListEmpty: boolean,
  isFolderListLoading: boolean,
  onPageChanged: (page: number) => void,
  onCloseSearchSection: () => void,
  onSortChanged: (sortList: SortList[]) => void,
  onRemoveDocument: (document: DocumentDetail) => void,
  onRefresh: (page: number) => void,
  dropCompletedTrigger: number,
  isSearchingOcr: boolean,
  exportSearchResultDocuments: () => void,
  isLoadingSearchResultFile: boolean,
  mainFrameLeftSectionWidth: number,
}

function SearchResultRenderer({ text }: { text: string }) {
  return <div dangerouslySetInnerHTML={{ __html: text }} />;
}

/**
 * メインフレームの右セクションのコンポーネント
 */
function MainFrameRightSection({
  folder,
  onChangingFolderPermissions,
  // ----------------------
  documentsAndSearchType,
  pagination,
  isSearching,
  highlightTexts,
  ocrHighlightTexts,
  isFolderListEmpty,
  isFolderListLoading,
  onPageChanged,
  onCloseSearchSection,
  onSortChanged,
  onRemoveDocument,
  onRefresh,
  dropCompletedTrigger,
  isSearchingOcr,
  exportSearchResultDocuments,
  isLoadingSearchResultFile,
  mainFrameLeftSectionWidth,
}: Props) {
  // refs
  const inputFile = useRef<HTMLInputElement | null>(null);
  const tableRef = useRef<AgGridReact<Document>>(null);
  const latestRequestedDocumentId = useRef<number | null>(null);

  // States
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);
  const [selectDocument, setSelectedDocument] = useState<Document | null>(null);
  const [labelExportDisplay, setLabelExportDisplay] = useState(false);
  const [modal, setModal] = useState<AlertModalProps>(alertModalInitialState);
  const [exportLabelDocuments, setExportLabelDocuments] = useState<SelectedFilesToDocumentWarehousing[]>([]);
  const [sortedIdList, setSortedIdList] = useState<number[]>([]);
  const [preventModalClose, setPreventModalClose] = useState(false);
  const [pdfForModal, setPdfForModal] = useState<File | null>(null);
  const [openBatchDocumentDownloadModal, setOpenBatchDocumentDownloadModal] = useState(false);
  const [openBatchPdfDownloadModal, setOpenBatchPdfDownloadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [suppressModal, setSuppressModal] = useState(false);
  const [detailedDocument, setDetailedDocument] = useState<DocumentDetail | null>(null);

  const {
    user, hasPermission, hasFolderPermission,
  } = useAuthContext();

  const selectedDocumentsRef = useRef<Document[]>(selectedDocuments);

  // Hooks
  const { onDragStart } = useDragAndDrop(folder, selectedDocumentsRef);
  const { displayMode } = useDocumentDisplayMode();
  const { allTags, reload: getAllTags, loadingAllTags } = useGetAllTagsApi();
  const navigate = useNavigate();

  const openMessageModal = useMessageModal();
  const documentGetDetail = useDocumentGetDetail();
  const { request: documentDownload, loading: documentDownloadLoading } = useDocumentDownload();
  const { request: createDownloadLog } = useCreateDownloadFileLogApi();
  const { request: documentBatchPdfDownload } = useDocumentBatchPdfDownload();
  const { request: reservationCartRegister } = useReservationCartRegister();

  // constants
  const cantGoNext = pagination.page === pagination.totalPages;
  const cantGoPrevious = pagination.page === 1;
  const isDocumentDraggable = true;
  const warehouseStatusCodeOrder = Object.keys(wareHouseRecords) as WarehouseStatusCode[];

  const { request: createLog } = useCreateLogApi();

  const getSelectedDocumentsWithPdfOrPreviousPdfOrAttachment = () => selectedDocuments.filter(({ fileName, previousPdfCount, attachmentFileCount }) => fileName || previousPdfCount || attachmentFileCount);

  const allTagsWithoutDuplicate = useMemo(() => {
    const set = new Set();
    return allTags.filter((tag) => {
      if (set.has(tag.tagLabel)) return false;
      set.add(tag.tagLabel);
      return true;
    });
  }, [allTags]);

  // Methods
  const onPdfPreviewRequest = useCallback((doc: Document) => {
    setSuppressModal(true);
    if (doc) {
      try {
        latestRequestedDocumentId.current = doc.id;
        documentGetDetail.request({ id: doc.id }).then(setDetailedDocument);
      } catch (e) {
        openMessageModal((e as ApiError)?.message);
      }
    }
  }, [documentGetDetail, openMessageModal]);

  useEffect(() => {
    if (detailedDocument) {
      if (detailedDocument.documents.length === 0) {
        openMessageModal('PDFファイルが見つかりませんでした。');
        return;
      }
      const current = detailedDocument.documents.sort((a, b) => b.versionNo - a.versionNo)[0];
      const documentId = detailedDocument.id;

      documentDownload({ documentDataId: current.dataId, fileName: current.fileName })
        .then((file) => {
          if (latestRequestedDocumentId.current === documentId) {
            createDownloadLog(current.dataId, 1);
            setPdfForModal(file);
          }
        })
        .catch(() => {
          if (latestRequestedDocumentId.current === documentId) {
            openMessageModal('PDFファイルが見つかりませんでした。');
          }
        });
    }
  }, [detailedDocument, documentDownload, openMessageModal, createDownloadLog]);

  const toggleSelectDocument = useCallback((document: Document) => {
    const selectedDocument = selectedDocuments.find((d) => d.id === document.id);
    if (selectedDocument) {
      setSelectedDocuments(selectedDocuments.filter((d) => d.id !== selectedDocument.id));
    } else {
      setSelectedDocuments([...selectedDocuments, document]);
    }
  }, [selectedDocuments]);

  const onFirstDataRendered = useCallback((e: FirstDataRenderedEvent<Document>) => e.api?.forEachNodeAfterFilter((node) => {
    node.setSelected(selectedDocuments.some((document) => node.data?.id === document.id), true, true);
  }), [selectedDocuments]);

  const selectedFilesToDocumentWarehousing = useCallback((): SelectedFilesToDocumentWarehousing[] => selectedDocuments.map((item) => ({
    ccControlNumber: item.ccControlNumber,
    documentId: item.id,
    documentName: item.name,
    documentFileName: item.fileName,
    documentFileSize: item.fileSize,
    documentTypeId: item.typeId,
    documentTypeName: item.typeName,
    tagList: item.tagList?.map((tag) => ({
      tagLabel: tag.label,
      tagValue: tag.value,
    })) ?? [],
    relationalDocumentCount: item.relationalDocumentCount,
    warehouseStatus: item.warehouseStatus,
    warehouseStatusCode: item.warehouseStatusCode,
    paperFlg: item.paperFlg !== undefined ? item.paperFlg : undefined,
    electronicFlg: item.electronicFlg !== undefined ? item.electronicFlg : undefined,
    registUser: item.registUser,
    registDate: item.registDate,
    updateUser: item.updateUser,
    updateDate: item.updateDate,
    itemCode: item.itemCode ?? '',
    barcodePrinting: item.barcodePrinting,
    cartAddedFlg: item.cartAddedFlg,
  })), [selectedDocuments]);

  const getRequestType = useCallback((controlCd: string): RequestType => {
    const requestTypes = {
      [ControlCode.Receiving]: RequestType.RECEIVE,
      [ControlCode.Warehousing]: RequestType.DISPATCH,
      [ControlCode.Destruction]: RequestType.DISPOSAL,
      [ControlCode.Scan]: RequestType.REMOTE_SCAN,
    };

    return requestTypes[controlCd as keyof typeof requestTypes];
  }, []);

  const updateGridRowsWithCartFlag = useCallback((documentListIds: { documentId: number }[]) => {
    if (!tableRef.current) return;

    const gridApi = tableRef.current.api;

    gridApi.forEachNode((node) => {
      const document = node.data as Document | undefined;
      const isInCart = documentListIds.some(({ documentId }) => documentId === document?.id);

      if (document && isInCart) {
        document.cartAddedFlg = 1;
        node.setData({ ...document });
      }
    });
  }, [tableRef]);

  const addToCart = useCallback(async (controlCd: string, documentList: SelectedFilesToDocumentWarehousing[]) => {
    const documentListIds = documentList.map(({ documentId }) => ({ documentId }));
    const requestType = getRequestType(controlCd);

    if (!requestType) return;

    try {
      const response = await reservationCartRegister({
        documentList: documentListIds,
        requestType,
      });

      updateGridRowsWithCartFlag(response.documentList);
      if (response.messageType === RegisterCartResponseMessageType.TOAST) {
        toast(response.message);
      }
      if (response.messageType === RegisterCartResponseMessageType.ALERT) {
        openMessageModal(response.message);
      }
    } catch (error) {
      openMessageModal((error as ApiError)?.message);
    }
  }, [getRequestType, reservationCartRegister, updateGridRowsWithCartFlag, openMessageModal]);

  const checkControlCodeAndDisplayMessage = async (controlCd: string) => {
    const documentWarehousingList = selectedFilesToDocumentWarehousing();
    const documentsToAdd: SelectedFilesToDocumentWarehousing[] = [];
    const documentsCanNotAdd: SelectedFilesToDocumentWarehousing[] = [];

    if ((controlCd === ControlCode.Scan || controlCd === ControlCode.Warehousing || controlCd === ControlCode.Destruction) && documentWarehousingList.length > 300) {
      openMessageModal(`${ControlCode.describe(controlCd)}依頼の受付上限は300件です。\n現在${documentWarehousingList.length}件選択されています。`);
      return;
    }

    if (controlCd === ControlCode.Receiving || controlCd === ControlCode.Scan || controlCd === ControlCode.Warehousing || controlCd === ControlCode.Destruction) {
      documentWarehousingList.forEach((document) => {
        documentsToAdd.push(document);
      });
    }
    if (controlCd === ControlCode.LabelExport) {
      documentWarehousingList.forEach(async (document) => {
        if (String(document.warehouseStatusCode) !== String(WarehouseStatusCode.NOT_RECEIVED)) {
          documentsToAdd.push(document);
        } else {
          documentsCanNotAdd.push(document);
        }
      });
      const sortedDocumentsToAdd: SelectedFilesToDocumentWarehousing[] = [];
      sortedIdList.forEach((id) => documentsToAdd.forEach((document) => {
        if (document.documentId === id) sortedDocumentsToAdd.push(document);
      }));

      setExportLabelDocuments(sortedDocumentsToAdd);
    }

    if (documentsCanNotAdd.length > 0) {
      if (controlCd === ControlCode.LabelExport && documentsToAdd.length === 0) {
        openMessageModal('ステータスが「未入庫」のため発行できません。\n対象文書をご確認ください。');
        return;
      }

      setModal({
        modalStyle: { width: '650px' },
        open: true,
        text: `保管品ラベルを発行しますか？\n\n対象件数：${documentsToAdd.length}件\n※ステータスが「未入庫」以外の文書が対象です。`,
        confirmText: '発行する',
        cancelText: 'キャンセル',
        cancelButtonFirst: true,
        onConfirm: async () => {
          if (documentsToAdd.length === 0) {
            setModal({ ...modal, open: false });
            return;
          }
          setLabelExportDisplay(true);
          setModal({ ...modal, open: false });
        },
        onCancel: async () => setModal({ ...modal, open: false }),
      });
    } else {
      if (controlCd === ControlCode.LabelExport) {
        setLabelExportDisplay(true);
        return;
      }

      await addToCart(controlCd, documentsToAdd);
    }
  };

  const { markText, unMarkText, highLightSearch } = useTextHighlighter({ selector: '.highlightTarget' });

  useEffect(() => {
    if (highlightTexts.length > 0) {
      markText(highlightTexts);
      return;
    }
    unMarkText();
  }, [highlightTexts, markText, unMarkText]);

  const relationalDocumentsDetailCellRenderer = (e: { data: Document, pinned: string }) => (
    <RelationalDocumentsDetailCellRenderer
      pinned={e.pinned}
      relationalDocumentList={e.data.relationalDocumentList}
    />
  );

  const onFileSelected = useCallback((value: FileList | null, folderParam: Folder | null) => {
    if (value && folderParam) {
      navigate(routes.csvImportScreen, { state: { file: value[0], folderId: folderParam.id } });
    }
  }, [navigate]);

  const handleBatchPdfDownloadClick = () => {
    const selectedDocumentsWithPdfOrPreviousPdfOrAttachment = getSelectedDocumentsWithPdfOrPreviousPdfOrAttachment();
    if (selectedDocumentsWithPdfOrPreviousPdfOrAttachment.length === 0) {
      openMessageModal('ダウンロード対象のファイルが見つかりませんでした。\n対象データをご確認ください。');
      return;
    }
    setOpenBatchPdfDownloadModal(true);
  };

  const handleLabelGenerate = async () => {
    await checkControlCodeAndDisplayMessage(ControlCode.LabelExport);
  };

  const onDropdownOptionSelected = (e: string) => {
    if (e === '一括登録') {
      inputFile.current?.click();
    }
    if (e === '文書を追加する') {
      if (folder) {
        navigate(routes.docRegisterScreen, { state: { folderId: folder.id } });
      }
    }
  };

  const closeModal = () => {
    if (preventModalClose) {
      setPreventModalClose(false);
      return;
    }
    setTimeout(() => {
      setSelectedDocument(null);
    }, 100);
  };

  const handleRemoveDocument = (document: DocumentDetail) => {
    onRemoveDocument(document);
    setSelectedDocuments([]);
    tableRef.current?.api?.deselectAll();
  };

  const refreshTableCells = useCallback(() => {
    tableRef.current?.api.refreshCells({
      force: true,
    });
  }, []);

  // Memos
  const addDocOption = useMemo(() => [
    { text: '文書を追加する', value: '文書を追加する' },
    { text: '一括登録', value: '一括登録' },
  ], []);

  const filteredDocuments = useMemo(() => {
    const result = documentsAndSearchType.documents;

    tableRef.current?.api.forEachNode((node) => node.setExpanded(false));
    refreshTableCells();

    return result;
  }, [documentsAndSearchType.documents]);

  const selectAndUpdateGridSelection = useCallback((document: Document) => {
    setSelectedDocuments((prev) => {
      const next = [...prev, document];
      selectedDocumentsRef.current = next;
      return next;
    });
    tableRef.current?.api.getRowNode(String(document.id))?.setSelected(true);
  }, [selectedDocumentsRef]);

  const handleDragStartSelect = useCallback((e: React.DragEvent<HTMLDivElement>, data: Document) => {
    const alreadySelected = selectedDocumentsRef.current.some((d) => d.id === data.id);
    const hasAnySelected = selectedDocumentsRef.current.length > 0;

    if (hasAnySelected && !alreadySelected) {
      selectAndUpdateGridSelection(data);
    }

    onDragStart(e, data);
  }, [onDragStart, selectAndUpdateGridSelection]);

  const dragEnabledCellRenderer = (params: ICellRendererParams<Document>) => {
    const data = params.node?.data;
    if (!data) return null;

    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          paddingLeft: '10px',
          paddingRight: '10px',
        }}
        draggable={data.requiredTagInputFlg === 1 ? isDocumentDraggable : false}
        onDragStart={(e) => handleDragStartSelect(e, data)}
      >
        <span>{params.value}</span>
      </div>
    );
  };

  const colsDefs = useMemo((): ColDef<Document>[] => {
    const cols: ColDef[] = [
      {
        headerName: '',
        field: 'cart',
        maxWidth: 30,
        lockPosition: true,
        suppressColumnsToolPanel: true,
        cellRenderer: renderCartIcon,
        pinned: 'left',
      },
      {
        headerName: 'Checkbox',
        field: 'checkbox',
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        maxWidth: 50,
        minWidth: 50,
        lockPosition: true,
        suppressColumnsToolPanel: true,
        pinned: 'left',
      },
      {
        headerName: '',
        field: 'relationalDocumentCount',
        maxWidth: 30,
        minWidth: 30,
        lockPosition: true,
        suppressColumnsToolPanel: true,
        cellStyle: { padding: '0', margin: '0' },
        cellClass: 'add-border-right',
        cellRendererSelector: renderRelatedDocumentIcon,
        pinned: 'left',
      },
      {
        headerName: '文書名',
        resizable: true,
        sortable: true,
        field: 'name',
        width: 250,
        cellStyle: { padding: '0', margin: '0' },
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params: ICellRendererParams) => (
          <div
            style={{
              alignContent: 'center', display: 'flex', paddingRight: 10, paddingLeft: 25,
            }}
            draggable={params.node.data.requiredTagInputFlg === 1 ? isDocumentDraggable : false}
            onDragStart={(e) => handleDragStartSelect(e, params.node.data)}
          >
            {params.node.data.fileName
              ? (
                <img
                  src="/images/Pdf-icon.svg"
                  alt="pdf"
                  onClick={() => onPdfPreviewRequest(params.node.data)}
                />
              )
              : (
                <img
                  src="/images/Pdf-icon-semitransparent.svg"
                  alt="pdf"
                  onClick={() => setSuppressModal(true)}
                />
              )}
            {isSearching ? (
              <div className={[mainStyles['ml-2'], mainStyles['text-overflow'], styles.tableRowHeight, styles.docName].join(' ')}>
                <div className={styles.folderName}>
                  {params.node.data.folderName}
                </div>
                <div
                  className={[mainStyles['ml-2'], mainStyles['text-overflow'], styles.docName, 'highlightTarget'].join(' ')}
                >
                  {params.value}
                </div>
              </div>
            ) : (
              <span className={[mainStyles['ml-2'], mainStyles['text-overflow'], 'highlightTarget'].join(' ')}>
                {params.value}
              </span>
            )}
            {params.node.data.requiredTagInputFlg !== 1 && (
            <div style={{ marginLeft: 'auto' }}>
              <img src="/images/Icon-awesome-exclamation.svg" alt="exclamation" style={{ height: '12px', marginRight: '5px' }} />
            </div>
            )}
          </div>
        ),
      },
      AGUtils.colDefault('typeName', '文書種類', undefined, undefined, undefined, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      {
        headerName: 'ステータス',
        field: 'warehouseStatus',
        width: 150,
        resizable: true,
        sortable: true,
        cellStyle: { padding: '0', margin: '0' },
        cellRenderer: dragEnabledCellRenderer,
        comparator: (_valueA, _valueB, nodeA, nodeB) => {
          const codeA = nodeA.data.warehouseStatusCode;
          const codeB = nodeB.data.warehouseStatusCode;

          const indexA = warehouseStatusCodeOrder.indexOf(codeA);
          const indexB = warehouseStatusCodeOrder.indexOf(codeB);

          const safeIndexA = indexA === -1 ? warehouseStatusCodeOrder.length : indexA;
          const safeIndexB = indexB === -1 ? warehouseStatusCodeOrder.length : indexB;

          return safeIndexA - safeIndexB;
        },
      },
      AGUtils.colDefault('fileName', 'PDFファイル名', 170, true, true, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      AGUtils.colFileSize('fileSize', 'サイズ', undefined, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      {
        headerName: '添付ファイル',
        resizable: true,
        sortable: true,
        field: 'attachmentFileCount',
        width: 120,
        cellStyle: { padding: '0', margin: '0' },
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params: ICellRendererParams) => (
          <div
            style={{
              width: '100%', height: '100%', paddingLeft: 10, paddingRight: 10,
            }}
            draggable={params.node.data.requiredTagInputFlg === 1 ? isDocumentDraggable : false}
            onDragStart={(e) => handleDragStartSelect(e, params.node.data)}
          >
            <button
              type="button"
              className={params.data?.attachmentFileCount ? styles.tableButton : styles.tableButtonDisabled}
            >
              <img width={12} src="/images/paperclip-solid-full.svg" alt="" className={styles.attachFileIcon} />
              {params.value}
            </button>
          </div>
        ),
      },
      {
        ...AGUtils.colElectronicFlg('electronicFlg', '契約種別', 100),
        cellStyle: { padding: '0', margin: '0' },
        cellRenderer: dragEnabledCellRenderer,
      },
      {
        ...AGUtils.colPaperFlg('paperFlg', '書面有無', 105),
        cellStyle: { padding: '0', margin: '0' },
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params: ICellRendererParams) => (
          <div
            style={{
              width: '100%', height: '100%', paddingRight: 10, paddingLeft: 10,
            }}
            draggable={params.node.data.requiredTagInputFlg === 1 ? isDocumentDraggable : false}
            onDragStart={(e) => handleDragStartSelect(e, params.node.data)}
          >
            <button
              type="button"
              className={params.data?.paperFlg ? styles.tableButton : styles.tableButtonDisabled}
            >
              書面
            </button>
          </div>
        ),
      },
      AGUtils.colDefault('ccControlNumber', '文書ID', undefined, undefined, undefined, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      AGUtils.colDefault('barcodePrinting', 'バーコード印字欄', 150, true, true, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      AGUtils.colDefault('itemCode', '保管品バーコード', 150, true, true, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      AGUtils.colUsername('registUser', '登録者', undefined, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      AGUtils.colDate('registDate', '登録日時', undefined, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      AGUtils.colUsername('updateUser', '更新者', undefined, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      AGUtils.colDate('updateDate', '更新日時', undefined, { cellRenderer: dragEnabledCellRenderer, cellStyle: { padding: '0', margin: '0' } }),
      {
        headerName: '検索結果',
        field: 'searchResult',
        cellClass: 'highlightTarget',
        colId: 'searchResult',
        resizable: true,
        width: 300,
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.value) return null;
          return (
            <div
              draggable={params.node.data.requiredTagInputFlg === 1 ? isDocumentDraggable : false}
              onDragStart={(e) => handleDragStartSelect(e, params.node.data)}
            >
              <SearchResultRenderer text={Formatter.escapeHtmlExceptMark(params.value)} />
            </div>
          );
        },
        lockVisible: true,
        suppressColumnsToolPanel: !user?.hasOcrSupport,
        initialHide: !isSearchingOcr,
      },
    ];

    const tags = new Set<string>(allTags.map((tag) => tag.tagLabel));
    const unique = Array.from(tags);

    unique.forEach((tag) => {
      const apiTag = allTags.find(({ tagLabel }) => (tagLabel === tag)) ?? null;
      cols.push({
        field: `${tag}`,
        headerName: tag,
        resizable: true,
        sortable: true,
        width: 160,
        cellClass: 'highlightTarget',
        valueGetter: AGUtils.getTagValueGetter(tag, apiTag),
        cellRenderer: dragEnabledCellRenderer,
        cellStyle: { padding: '0', margin: '0' },
        comparator: AGUtils.customTagComparator(apiTag),
      });
    });

    return cols;
  }, [allTags, folder, onDragStart, isDocumentDraggable, isSearching, isSearchingOcr]);

  const applyPinnedColumns = useCallback(() => {
    if (tableRef.current) {
      tableRef.current.columnApi.applyColumnState({
        state: [
          { colId: 'cart', pinned: 'left' },
          { colId: 'checkbox', pinned: 'left' },
          { colId: 'relationalDocumentCount', pinned: 'left' },
        ],
        applyOrder: true,
      });
    }
  }, [tableRef]);

  const onSelectDocument = (document: Document) => {
    if (suppressModal) {
      setSelectedDocument(null);
      setTimeout(() => {
        setSuppressModal(false);
      }, 200);
    } else if (document) setSelectedDocument(document);
  };

  const onTableRowClicked = (e: RowClickedEvent) => {
    const isRelationalDocumentCell = tableRef.current?.api?.getFocusedCell()?.column?.getColId() === 'relationalDocumentCount';
    if (!isRelationalDocumentCell) {
      onSelectDocument(e.data);
    }
  };

  const handleSortedData = (data: Document[]) => {
    const ids = data.map((d) => d.id);
    setSortedIdList(ids);
  };

  const noRowsText = useMemo(() => {
    if (isFolderListLoading || filteredDocuments.length !== 0) return '　';
    if (isFolderListEmpty) return 'フォルダを作成してください';
    if (!folder && !isSearching) return 'フォルダを選択してください';
    return STATUS_MESSAGES.NO_DATA;
  }, [folder, isFolderListEmpty, isFolderListLoading, filteredDocuments, isSearching]);

  const handleBatchPdfDownload = (targetDocuments: Document[], isPdfDownload: boolean, isAttachmentDownload: boolean, incOldVerFlg: boolean, nameRules?: BatchPdfDownloadNameRules) => {
    documentBatchPdfDownload({
      documentIdList: targetDocuments.map(({ id: documentId }) => ({ documentId })),
      isPdfDownload: Number(isPdfDownload),
      isAttachmentDownload: Number(isAttachmentDownload),
      incOldVerFlg: Number(incOldVerFlg),
      nameRules,
    });
    setOpenBatchPdfDownloadModal(false);

    // This was requested by the client because this request could take a long time sending confirmation email to the users
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      openMessageModal('リクエストを受け付けました。\n準備ができ次第、メールでお知らせします。');
    }, 1000);
  };

  const handleSubmitBatchPdfDownload = (isPdfDownload: boolean, isAttachmentDownload: boolean, incOldVerFlg: boolean, nameRules?: BatchPdfDownloadNameRules) => {
    const targetDocuments = getSelectedDocumentsWithPdfOrPreviousPdfOrAttachment().filter(
      ({ fileName, previousPdfCount, attachmentFileCount }) => (
        (isPdfDownload && fileName)
        || (incOldVerFlg && previousPdfCount)
        || (isAttachmentDownload && attachmentFileCount)
      ),
    );

    if (targetDocuments.length === 0) {
      openMessageModal('ダウンロード対象のファイルが見つかりませんでした。\n対象データをご確認ください。');
      return;
    }

    setModal({
      open: true,
      text: 'ダウンロード処理を実行します。よろしいですか？\n※他の管理ユーザーにも通知されます。',
      onConfirm: () => {
        handleBatchPdfDownload(targetDocuments, isPdfDownload, isAttachmentDownload, incOldVerFlg, nameRules);
        setModal(alertModalInitialState);
      },
      onCancel: () => {
        setModal(alertModalInitialState);
      },
      cancelText: 'キャンセル',
      confirmText: '実行する',
      cancelButtonFirst: true,
    });
  };

  const handleBatchDownload = () => {
    exportSearchResultDocuments();
    setOpenBatchDocumentDownloadModal(false);
  };

  const handleSubmitBatchDownload = () => {
    setModal({
      open: true,
      text: 'ダウンロード処理を実行します。よろしいですか？\n※他の管理ユーザーにも通知されます。',
      onConfirm: () => {
        handleBatchDownload();
        setModal(alertModalInitialState);
      },
      onCancel: () => {
        setModal(alertModalInitialState);
      },
      cancelText: 'キャンセル',
      confirmText: '実行する',
      cancelButtonFirst: true,
    });
  };

  const processCellCallback = (params: ProcessCellForExportParams<Document>) => {
    const colDef = params.column.getColDef();
    if (colDef.colId === 'searchResult') {
      return Formatter.removeMarkTags(params.value);
    }
    return params.value;
  };

  // Effects

  useEffectOnce(() => {
    getAllTags();
  });

  useEffect(() => {
    setSelectedDocuments([]);
    tableRef.current?.api?.deselectAll();
  }, [folder]);

  useEffect(() => {
    closeModal();
  }, [folder, selectedDocuments, isSearching, documentsAndSearchType]);

  useEffect(() => {
    if (documentsAndSearchType.searchType === SearchType.SIMPLE) {
      highLightSearch(highlightTexts);
    }
  }, [documentsAndSearchType.searchType, highLightSearch, highlightTexts]);

  useEffect(() => {
    setSelectedDocuments([]);
    tableRef.current?.api?.deselectAll();
  }, [dropCompletedTrigger]);

  useEffect(() => {
    selectedDocumentsRef.current = selectedDocuments;
  }, [selectedDocuments]);

  useEffect(() => {
    if (tableRef.current) {
      tableRef?.current?.columnApi?.setColumnsVisible(['searchResult'], isSearchingOcr);
    }
  }, [isSearchingOcr]);

  return (
    <div className={styles.mainFrameRightSection} style={{ width: `calc(100vw - ${mainFrameLeftSectionWidth + 63}px)` }}>
      <div className="d-flex" style={{ minWidth: selectedDocuments.length > 0 ? '1141px' : '0' }}>
        {!isSearching && (
        <Breadcrumb crumbs={[{ label: `${folder?.name ? folder.name : ''}` }]} />
        )}

        {isSearching && (
        <div className={styles.box}>
          <div className={styles.boxLeft}>
            <div className={styles.boxLeftTitle}>
              <span>検索結果</span>
            </div>
          </div>
          <div className={styles.boxRight}>
            <div
              role="button"
              tabIndex={0}
              className={styles.boxRightButton}
              onClick={onCloseSearchSection}
            >
              結果を閉じる
            </div>
          </div>
        </div>
        )}
      </div>

      <input
        type="file"
        id="file"
        ref={inputFile}
        style={{ display: 'none' }}
        onChange={(e) => onFileSelected(e.target.files, folder)}
        accept=".csv"
      />
      <div
        className={[styles.mainFrameRightSectionTop, selectedDocuments.length > 0 ? styles.topMinWidth : ''].join(' ')}
      >
        {(selectedDocuments.length === 0) && (
        <div className={styles.mainFrameRightSectionTopButtons}>
          {!isSearching && (
          <>
            {folder && (
            <DropdownMenu
              className={styles.docDropdownMenu}
              options={addDocOption}
              left
              onSelect={onDropdownOptionSelected}
              disabled={!hasFolderPermission(folder, ControlCode.Write)}
            >
              <Button
                onClick={closeModal}
                className={styles.addDocButton}
                disabled={!hasFolderPermission(folder, ControlCode.Write)}
                size="smaller"
              >
                <>
                  <img className={styles.addDocIcon} src={hasFolderPermission(folder, ControlCode.Write) ? '/images/Add-Plus.svg' : '/images/Add-Plus-Disabled.svg'} alt="" />
                  <span>文書を追加する</span>
                </>
              </Button>
            </DropdownMenu>
            )}
            {(user?.managementType === ManagementType.Admin && folder) && (
            <Button
              className={`${mainStyles['ml-10']} ${mainStyles['justify-content-center']}`}
              size="smaller"
              onClick={() => onChangingFolderPermissions()}
            >
              権限を変更する
            </Button>
            )}
          </>
          )}
          {(isSearching || folder) && (
            <Button
              className={styles.documentDownloadButton}
              size="smaller"
              color="lighterGray"
              disabled={filteredDocuments.length === 0}
              onClick={() => setOpenBatchDocumentDownloadModal(true)}
            >
              文書リストダウンロード
            </Button>
          )}
          {/* Keep this button hidden for now. This was a client request */}
          {/* {folder && ( */}
          {/* <Button */}
          {/*  outline */}
          {/*  className={styles.listButton} */}
          {/*  onClick={toggleDisplayMode} */}
          {/* /> */}
          {/* )} */}
        </div>
        )}
        {(selectedDocuments.length > 0) && (
          <div className={styles.onRowSelected}>
            <div className={styles.totalSelected}>
              選択中：
              {selectedDocuments.length}
              件
            </div>
            <div className={styles.addToCartContainer}>
              <Button
                className={styles.addToCartButton}
                disabled={!hasPermission(ControlCode.Receiving)}
                size="smaller"
                onClick={() => checkControlCodeAndDisplayMessage(ControlCode.Receiving)}
              >
                入庫
              </Button>
              <Button
                className={styles.addToCartButton}
                disabled={!hasPermission(ControlCode.Scan)}
                size="smaller"
                onClick={() => checkControlCodeAndDisplayMessage(ControlCode.Scan)}
              >
                スキャン
              </Button>
              <Button
                className={styles.addToCartButton}
                disabled={!hasPermission(ControlCode.Warehousing)}
                size="smaller"
                onClick={() => checkControlCodeAndDisplayMessage(ControlCode.Warehousing)}
              >
                出庫
              </Button>
              <Button
                className={styles.addToCartButton}
                disabled={!hasPermission(ControlCode.Destruction)}
                size="smaller"
                onClick={() => checkControlCodeAndDisplayMessage(ControlCode.Destruction)}
              >
                廃棄
              </Button>
            </div>
            <div className={styles.actionButtonsContainer}>
              <Button
                className={styles.batchPdfDownloadButton}
                size="smaller"
                color="lighterGray"
                onClick={handleBatchPdfDownloadClick}
              >
                ファイルダウンロード
              </Button>
              <Button
                size="smaller"
                color="lighterGray"
                onClick={handleLabelGenerate}
              >
                保管品ラベル発行
              </Button>
            </div>
          </div>
        )}
      </div>
      { displayMode === 'list' && (
      <BaseTable<Document>
        tableRef={tableRef}
        formName={mainPageTableName}
        waitToLoad={loadingAllTags}
        rowData={filteredDocuments}
        columnDefs={colsDefs}
        rowSelection="multiple"
        suppressRowClickSelection
        onSelectionChanged={(e) => setSelectedDocuments(e.api.getSelectedRows())}
        onRowClicked={(e) => onTableRowClicked(e)}
        onFirstDataRendered={onFirstDataRendered}
        minWidthProp={selectedDocuments.length > 0 ? '1141px' : '0'}
        sideBar
        pagination
        paginationPageSize={500}
        suppressPaginationPanel
        masterDetail
        detailCellRenderer={relationalDocumentsDetailCellRenderer}
        detailRowAutoHeight
        embedFullWidthRows
        suppressMenuHide={false}
        rowDragManaged
        rowDragMultiRow
        sortChangedHandler={onSortChanged}
        handleSortedData={handleSortedData}
        onRowDataUpdated={() => { highLightSearch(highlightTexts); }}
        onBodyScroll={() => { highLightSearch(highlightTexts); }}
        noRowsText={noRowsText}
        createLogOnDownloadTableData={LogFormName.Main}
        useColPin
        onColumnSettingsApplied={applyPinnedColumns}
        getRowId={(params: GetRowIdParams<Document>) => String(params.data.id)}
        hideSearchResultColumn={!isSearchingOcr}
        processCellForClipboard={processCellCallback}
        baseExportParams={{
          processCellCallback,
        }}
      />
      )}
      {displayMode === 'grid' && (
      <div className={[styles.fileGridContainer].join(' ')}>
        {
              filteredDocuments.map((document) => (
                <div
                  className={[mainStyles['p-2'], styles.fileCard].join(' ')}
                  key={document.id}
                  draggable={isDocumentDraggable}
                  onDragStart={(e) => handleDragStartSelect(e, document)}
                >
                  <Checkbox
                    className={styles.fileCardCheckbox}
                    value={selectedDocuments.some((item) => item.id === document.id)}
                    onChange={() => toggleSelectDocument(document)}
                  />
                  <div
                    className={styles.fileCardImageContainer}
                    aria-hidden
                    onClick={() => onSelectDocument(document)}
                  >
                    {document.fileName
                      ? <img src={PdfIcon} alt="pdf" height={50} />
                      : <div style={{ height: '50px' }} />}
                  </div>
                  <span className={styles.fileCardTitle}>{document.name}</span>
                </div>
              ))
          }
      </div>
      )}
      {displayMode === 'largeGrid' && (
      <div className={[styles.fileGridContainer].join(' ')}>
        {
              filteredDocuments.map((document) => (
                <div
                  className={[mainStyles['p-2'], styles.fileCardLarge].join(' ')}
                  key={document.id}
                  draggable={isDocumentDraggable}
                  onDragStart={(e) => handleDragStartSelect(e, document)}
                >
                  <Checkbox
                    className={styles.fileCardCheckbox}
                    value={selectedDocuments.some((item) => item.id === document.id)}
                    onChange={() => toggleSelectDocument(document)}
                  />
                  <div
                    className={styles.fileCardImageContainer}
                    aria-hidden
                    onClick={() => onSelectDocument(document)}
                  >
                    {document.fileName
                      ? <img src={PdfIcon} alt="pdf" height={100} />
                      : <div style={{ height: '100px' }} />}
                  </div>
                  <span className={styles.fileCardTitle}>{document.name}</span>
                </div>
              ))
          }
      </div>
      )}
      { pagination.totalItems > 0 && (
      <PaginationJp
        totalPage={pagination.totalPages}
        pageInfo={{ pageLimit: pagination.pageLimit, page: pagination.page, total: pagination.totalItems }}
        containerStyle={{
          minWidth: selectedDocuments.length > 0 ? '1101px' : '0',
        }}
        onBtFirst={() => {
          if (cantGoPrevious) return;
          onPageChanged(1);
        }}
        onBtLast={() => {
          if (cantGoNext) return;
          onPageChanged(pagination.totalPages);
        }}
        onBtNext={() => {
          if (cantGoNext) return;
          onPageChanged(pagination.page + 1);
        }}
        onBtPrevious={() => {
          if (cantGoPrevious) return;
          onPageChanged(pagination.page - 1);
        }}
      />
      )}
      {selectDocument && (
      <DocDetailModalFrame
        document={selectDocument}
        onClose={closeModal}
        onRemoveDocument={handleRemoveDocument}
        onDocumentUpdated={() => onRefresh(pagination.page)}
        onCancelAddingRelatedDocuments={() => {
          setPreventModalClose(true);
          onRefresh(pagination.page);
        }}
        onRemoveDocumentVersion={() => {
          setPreventModalClose(true);
          onRefresh(pagination.page);
        }}
        highlightTexts={ocrHighlightTexts}
        searchKeyword={documentsAndSearchType.searchKeyword}
      />
      )}
      {labelExportDisplay && (
      <ExportLabelComponent
        data={exportLabelDocuments}
        display={labelExportDisplay}
        setModalDisplay={setLabelExportDisplay}
        tableName="mainPageExportLabelComponent"
      />
      )}
      <AlertModal
        open={modal.open}
        text={modal.text}
        onCancel={modal.onCancel}
        onConfirm={modal.onConfirm}
        modalStyle={modal.modalStyle}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        cancelButtonFirst={modal.cancelButtonFirst}
      />
      { (detailedDocument && pdfForModal) && (
        <PdfPreviewModal
          open
          onClose={() => setPdfForModal(null)}
          pdfFile={pdfForModal}
          fileName={detailedDocument?.documents[0].fileName}
          fileId={detailedDocument?.documents[0].dataId}
          showOcrSearchPreview={detailedDocument?.documents[0].documentFileOcrFlg === true}
          documentVersionNo={detailedDocument?.documents[0].versionNo}
          documentId={detailedDocument?.id}
          highlightTexts={ocrHighlightTexts}
          searchKeyword={documentsAndSearchType.searchKeyword}
        />
      )}
      {openBatchDocumentDownloadModal && (
        <BatchDocumentDownloadModal
          totalDocumentQuantity={pagination.totalItems}
          onCancel={() => setOpenBatchDocumentDownloadModal(false)}
          onSubmit={handleSubmitBatchDownload}
        />
      )}
      {openBatchPdfDownloadModal && (
        <BatchPdfDownloadModal
          tags={allTagsWithoutDuplicate}
          totalDocumentQuantity={selectedDocuments.length}
          onCancel={() => setOpenBatchPdfDownloadModal(false)}
          onSubmit={handleSubmitBatchPdfDownload}
        />
      )}

      {(isLoading || documentDownloadLoading || isLoadingSearchResultFile) ? <LoadingOverlay /> : null}
    </div>
  );
}

export default MainFrameRightSection;
